import {AfterViewInit, Component, HostListener, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {NgModel} from '@angular/forms';
import {TemperatureSensorHelperModel} from '../../../../../../models/temperature-sensor-helper-model';
import {OptionModel} from '../../../../../../models/option-model'
import {TemperatureSensorNameEnum} from '../../../../../../enums/temperature-sensor-name-enum';
import {TemperatureSensorOptionEnum} from '../../../../../../enums/temperature-sensor-option-enum';
import {SensorModeType} from '../../../../../../enums/sensor-mode-type';
import {AutoSaveService} from '../../../../../../services/auto-save.service';
import {Subscription} from 'rxjs';
import {TemperatureSensorNameShortEnum} from '../../../../../../enums/temperature-sensor-name-short-enum';
import {DeviceDataService} from "../../../../../../services/device-data.service";
import {IDeviceData} from "../../../../../../interfaces/juconnGateway.response";
import {debounceTime} from "rxjs/operators";
import {ShareService} from '../../../../../../services/share.service';
import {ValidationService} from '../../../../../../services/validation.service';
import {SensorsTemperatureModel} from '../../../../../../models/data-models/sensors-temperature-model';


@Component({
  selector: 'app-install-sensors-temperature',
  templateUrl: './install-sensors-temperature.component.html',
  styleUrls: ['./install-sensors-temperature.component.scss'],

})
export class InstallSensorsTemperatureComponent implements OnInit, AfterViewInit, OnDestroy {
  public model: SensorsTemperatureModel;

  public sensorIdOptions: OptionModel[] = [];
  public sensorNameOptions: OptionModel[] = [];

  public readonly LOW_IMAGE_QUALITY = AutoSaveService.LOW_IMAGE_QUALITY;

  private readonly MIN_INDEX = 1;

  private MAX_NUMBER_OF_SINGLES: number;
  private MAX_NUMBER_OF_PAIRS: number;
  private MAX_NUMBER_OF_TRIOS: number;

  @ViewChildren(NgModel) dynamicInputs: QueryList<NgModel>;
  private inputSubscriptions: Subscription[] = [];

  private deviceDataSub: Subscription;
  private modelSub: Subscription;

  public isSmallScreen: boolean;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.isSmallScreen = event.target.innerWidth < 501;
  }

  constructor(
    private deviceDataService: DeviceDataService,
    private shareService: ShareService,
    public validationService: ValidationService
  ) {
    this.isSmallScreen = window.innerWidth < 500;
    this.modelSub = this.shareService.sensorsTemperature$.subscribe(data => {
      this.model = data || new SensorsTemperatureModel();
    });
    this.model = this.shareService.sensorsTemperature || new SensorsTemperatureModel();
    this.deviceDataSub = this.deviceDataService.deviceData$.subscribe(data => {
      if (data?.deviceData) {
        this.prepareSensorIdOptions(data.deviceData);
        this.initOptions();
      }
    });
  }

  ngOnDestroy(): void {
    this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.deviceDataSub?.unsubscribe();
    this.modelSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.updatedSensorIdOptionsVisibility();
    this.updateSensorNamesOptionsVisibility();
  }


  ngAfterViewInit(): void {
    this.dynamicInputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.publishModel();
      }));
    });

    // Subscribe to changes in the list of inputs
    // (it is called if some input is added or removed from the form/page,
    // for example, because of *ngIf condition changes)
    this.dynamicInputs.changes.subscribe(() => {
      // Unsubscribe from old subscriptions
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      // Subscribe to new inputs
      this.dynamicInputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.publishModel();
        }));
      });
    });
  }

  private initOptions() {
    // WARM WATER enums
    // WARM_WATER_TEMP_CIRCULATION
    this.prepareSingleSensor(TemperatureSensorNameEnum.WARM_WATER_TEMP_CIRCULATION,
      TemperatureSensorNameShortEnum.WARM_WATER_TEMP_CIRCULATION,
      TemperatureSensorOptionEnum.WARM_WATER_TEMP_CIRCULATION);

    // WARM_WATER_TEMP_BUFFER
    this.prepareSingleSensor(TemperatureSensorNameEnum.WARM_WATER_TEMP_BUFFER,
      TemperatureSensorNameShortEnum.WARM_WATER_TEMP_BUFFER,
      TemperatureSensorOptionEnum.WARM_WATER_TEMP_BUFFER);

    // WARM_WATER_TEMP_BOILER
    this.prepareSingleSensor(TemperatureSensorNameEnum.WARM_WATER_TEMP_BOILER,
      TemperatureSensorNameShortEnum.WARM_WATER_TEMP_BOILER,
      TemperatureSensorOptionEnum.WARM_WATER_TEMP_BOILER);

    // WARM_WATER_TEMP_SOLAR
    this.prepareSingleSensor(TemperatureSensorNameEnum.WARM_WATER_TEMP_SOLAR,
      TemperatureSensorNameShortEnum.WARM_WATER_TEMP_SOLAR,
      TemperatureSensorOptionEnum.WARM_WATER_TEMP_SOLAR);

    // WARM_WATER_TEMP_OUTLET
    this.prepareSingleSensor(TemperatureSensorNameEnum.WARM_WATER_TEMP_OUTLET,
      TemperatureSensorNameShortEnum.WARM_WATER_TEMP_OUTLET,
      TemperatureSensorOptionEnum.WARM_WATER_TEMP_OUTLET);

    // CUSTOM_WATER
    this.prepareSingleSensor(TemperatureSensorNameEnum.CUSTOM_WATER,
      TemperatureSensorNameShortEnum.CUSTOM_WATER,
      TemperatureSensorOptionEnum.CUSTOM_WATER);

    // FRESH_WATER_STATION_PRIMARY
    for (let i = this.MIN_INDEX; i <= this.MAX_NUMBER_OF_TRIOS; i++) {

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i) === -1) {
        this.sensorNameOptions.push({
          label: TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i,
          labelShort: TemperatureSensorNameShortEnum.FRESH_WATER_STATION_PRIMARY + i,
          value: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY + '_' + i,
          disabled: i !== this.MIN_INDEX,
          canBeActivated: true,
          index: i,
          sensorType: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY
        });
      }

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.FLOW_TEMP) === -1) {
        this.sensorNameOptions.push({
          label: TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.FLOW_TEMP,
          labelShort: TemperatureSensorNameShortEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameShortEnum.FLOW_TEMP,
          value: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY + '_' + i + TemperatureSensorOptionEnum.FLOW_TEMP,
          disabled: true,
          canBeActivated: false,
          index: i,
          sensorType: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY
        });
      }

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.RETURN_TEMP) === -1) {
        this.sensorNameOptions.push({
          label: TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.RETURN_TEMP,
          labelShort: TemperatureSensorNameShortEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameShortEnum.RETURN_TEMP,
          value: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY + '_' + i + TemperatureSensorOptionEnum.RETURN_TEMP,
          disabled: true,
          canBeActivated: false,
          index: i,
          sensorType: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY
        });
      }

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.FRESH_WATER_TEMP) === -1) {
        this.sensorNameOptions.push({
          label: TemperatureSensorNameEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameEnum.FRESH_WATER_TEMP,
          labelShort: TemperatureSensorNameShortEnum.FRESH_WATER_STATION_PRIMARY + i + TemperatureSensorNameShortEnum.FRESH_WATER_TEMP,
          value: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY + '_' + i + TemperatureSensorOptionEnum.FRESH_WATER_TEMP,
          disabled: true,
          canBeActivated: false,
          index: i,
          sensorType: TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY
        });
      }
    }

    // TEMPERATURE enums
    // HEATER_CL
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_CL,
      TemperatureSensorNameShortEnum.HEATER_CL, TemperatureSensorOptionEnum.HEATER_CL);

    // HEATER_BL
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_BL,
      TemperatureSensorNameShortEnum.HEATER_BL, TemperatureSensorOptionEnum.HEATER_BL);

    // HEATER_BL_SECONDARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_BL_SECONDARY,
      TemperatureSensorNameShortEnum.HEATER_BL_SECONDARY, TemperatureSensorOptionEnum.HEATER_BL_SECONDARY);

    // HEATER_DH_PRIMARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_DH_PRIMARY,
      TemperatureSensorNameShortEnum.HEATER_DH_PRIMARY, TemperatureSensorOptionEnum.HEATER_DH_PRIMARY);

    // HEATER_DH_SECONDARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_DH_SECONDARY,
      TemperatureSensorNameShortEnum.HEATER_DH_SECONDARY, TemperatureSensorOptionEnum.HEATER_DH_SECONDARY);

    // HEATER_SL_PRIMARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_SL_PRIMARY,
      TemperatureSensorNameShortEnum.HEATER_SL_PRIMARY, TemperatureSensorOptionEnum.HEATER_SL_PRIMARY);

    // HEATER_SL_SECONDARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_SL_SECONDARY,
      TemperatureSensorNameShortEnum.HEATER_SL_SECONDARY, TemperatureSensorOptionEnum.HEATER_SL_SECONDARY);

    // HEATER_BLC
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_BLC,
      TemperatureSensorNameShortEnum.HEATER_BLC, TemperatureSensorOptionEnum.HEATER_BLC);

    // HEATER_BLC_SECONDARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_BLC_SECONDARY,
      TemperatureSensorNameShortEnum.HEATER_BLC_SECONDARY, TemperatureSensorOptionEnum.HEATER_BLC_SECONDARY);

    // HEATER_WHLC
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_WHLC,
      TemperatureSensorNameShortEnum.HEATER_WHLC, TemperatureSensorOptionEnum.HEATER_WHLC);

    // HEATER_WHLC_SECONDARY
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_WHLC_SECONDARY,
      TemperatureSensorNameShortEnum.HEATER_WHLC_SECONDARY, TemperatureSensorOptionEnum.HEATER_WHLC_SECONDARY);

    // HEATER_BRINE_LOOP
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_BRINE_LOOP,
      TemperatureSensorNameShortEnum.HEATER_BRINE_LOOP, TemperatureSensorOptionEnum.HEATER_BRINE_LOOP);

    // HEATER_HPL
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_HPL,
      TemperatureSensorNameShortEnum.HEATER_HPL, TemperatureSensorOptionEnum.HEATER_HPL);
    // HEATER_LH
    this.preparePairSensor(TemperatureSensorNameEnum.HEATER_LH,
      TemperatureSensorNameShortEnum.HEATER_LH, TemperatureSensorOptionEnum.HEATER_LH);

    // CUSTOM_LOOP
    this.preparePairSensor(TemperatureSensorNameEnum.CUSTOM_LOOP,
      TemperatureSensorNameShortEnum.CUSTOM_LOOP, TemperatureSensorOptionEnum.CUSTOM_LOOP);

    // COLLECTION_LOOP
    this.preparePairSensor(TemperatureSensorNameEnum.COLLECTION_LOOP,
      TemperatureSensorNameShortEnum.COLLECTION_LOOP, TemperatureSensorOptionEnum.COLLECTION_LOOP);

    // console.log('sensorNameOptions', this.sensorNameOptions);
    this.sensorNameOptions = this.sensorNameOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  onSensorNameChanged($event: string, helperIndex: number) {
    setTimeout(_ => {
      const sensorType = this.parseTypeOutOfName($event);
      let modeType: SensorModeType = null;
      switch (sensorType) {
        // Singles
        case TemperatureSensorOptionEnum.WARM_WATER_TEMP_CIRCULATION:
          modeType = SensorModeType.SINGLE;
          break;
        case TemperatureSensorOptionEnum.WARM_WATER_TEMP_BUFFER:
          modeType = SensorModeType.SINGLE;
          break;
        case TemperatureSensorOptionEnum.WARM_WATER_TEMP_BOILER:
          modeType = SensorModeType.SINGLE;
          break;
        case TemperatureSensorOptionEnum.WARM_WATER_TEMP_SOLAR:
          modeType = SensorModeType.SINGLE;
          break;
        case TemperatureSensorOptionEnum.WARM_WATER_TEMP_OUTLET:
          modeType = SensorModeType.SINGLE;
          break;
        case TemperatureSensorOptionEnum.CUSTOM_WATER:
          modeType = SensorModeType.SINGLE;
          break;
        // Pairs
        case TemperatureSensorOptionEnum.HEATER_CL:
          modeType = SensorModeType.PAIR;
          break;
        case TemperatureSensorOptionEnum.HEATER_BL:
          modeType = SensorModeType.PAIR;
          break;
        case TemperatureSensorOptionEnum.HEATER_BL_SECONDARY:
          modeType = SensorModeType.PAIR;
          break;
        case TemperatureSensorOptionEnum.HEATER_DH_PRIMARY:
          modeType = SensorModeType.PAIR;
          break;
        case  TemperatureSensorOptionEnum.HEATER_DH_SECONDARY:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_SL_PRIMARY:
          modeType = SensorModeType.PAIR;
          break;
        case  TemperatureSensorOptionEnum.HEATER_SL_SECONDARY :
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_BLC:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_BLC_SECONDARY:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_WHLC:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_WHLC_SECONDARY:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_BRINE_LOOP:
          modeType = SensorModeType.PAIR;
          break
        case   TemperatureSensorOptionEnum.HEATER_HPL:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.HEATER_LH:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.CUSTOM_LOOP:
          modeType = SensorModeType.PAIR;
          break;
        case   TemperatureSensorOptionEnum.COLLECTION_LOOP:
          modeType = SensorModeType.PAIR;
          break;
        // Trios
        case TemperatureSensorOptionEnum.FRESH_WATER_STATION_PRIMARY:
          modeType = SensorModeType.TRIO;
          break;
      }

      let index = this.parseIndexOutOfName($event);

      this.model.temperatureSensorsHelperList[helperIndex].index = index;
      this.model.temperatureSensorsHelperList[helperIndex].pairMode = modeType;
      this.model.temperatureSensorsHelperList[helperIndex].type = sensorType;

      switch (modeType) {
        case SensorModeType.SINGLE:
          this.model.temperatureSensorsHelperList[helperIndex].name = $event;
          this.model.temperatureSensorsHelperList[helperIndex].name2 = null;
          this.model.temperatureSensorsHelperList[helperIndex].name3 = null;
          break;
        case SensorModeType.PAIR:
          this.model.temperatureSensorsHelperList[helperIndex].name = $event + TemperatureSensorOptionEnum.FLOW_TEMP;
          this.model.temperatureSensorsHelperList[helperIndex].name2 = $event + TemperatureSensorOptionEnum.RETURN_TEMP;
          this.model.temperatureSensorsHelperList[helperIndex].name3 = null;
          break;
        case SensorModeType.TRIO:
          this.model.temperatureSensorsHelperList[helperIndex].name = $event + TemperatureSensorOptionEnum.FLOW_TEMP;
          this.model.temperatureSensorsHelperList[helperIndex].name2 = $event + TemperatureSensorOptionEnum.RETURN_TEMP;
          this.model.temperatureSensorsHelperList[helperIndex].name3 = $event + TemperatureSensorOptionEnum.FRESH_WATER_TEMP;
          break;
      }

      this.updateSensorNamesOptionsVisibility();
      this.dynamicInputs.forEach(dInput => dInput.control.updateValueAndValidity())
    }, 0);
  }

  onAddSensorBtnClicked() {
    this.model.temperatureSensorsHelperList.push({} as TemperatureSensorHelperModel);
    this.publishModel();
  }


  onRemoveBtnClicked(i: number) {
    this.model.temperatureSensorsHelperList.splice(i, 1);
    this.updateSensorNamesOptionsVisibility();
    this.updatedSensorIdOptionsVisibility();
    this.publishModel();
  }


  private updateSensorNamesOptionsVisibility() {

    const currentIndices = {
      warm_water_temp_circulation: 1,
      warm_water_temp_buffer: 1,
      warm_water_temp_boiler: 1,
      warm_water_temp_solar: 1,
      fresh_water_station_primary: 1,
      warm_water_temp_outlet: 1,
      custom_water: 1,

      heater_cl: 1,
      heater_bl: 1,
      heater_bl_secondary: 1,
      heater_dh_primary: 1,
      heater_dh_secondary: 1,
      heater_sl_primary: 1,
      heater_sl_secondary: 1,
      heater_blc: 1,
      heater_blc_secondary: 1,
      heater_whlc: 1,
      heater_whlc_secondary: 1,
      heater_brine_loop: 1,
      heater_hpl: 1,
      heater_lh: 1,
      custom_loop: 1,
      collection_loop: 1
    }

    this.model.temperatureSensorsHelperList.forEach(sensorHelper => {
      if (currentIndices[sensorHelper.type] !== sensorHelper.index) {
        if (sensorHelper.name) {
          sensorHelper.name = sensorHelper.name.replace(sensorHelper.index + '', currentIndices[sensorHelper.type] + '');
        }
        if (sensorHelper.name2) {
          sensorHelper.name2 = sensorHelper.name2.replace(sensorHelper.index + '', currentIndices[sensorHelper.type] + '');
        }
        if (sensorHelper.name3) {
          sensorHelper.name3 = sensorHelper.name3.replace(sensorHelper.index + '', currentIndices[sensorHelper.type] + '');
        }
        sensorHelper.index = currentIndices[sensorHelper.type];
      }
      currentIndices[sensorHelper.type]++;
    });

    this.sensorNameOptions.filter(item => item.canBeActivated).forEach(option => {
      if (option.index === currentIndices[option.sensorType]) {
        option.disabled = false;
      } else {
        option.disabled = true;
      }
    })

    this.sensorNameOptions = this.sensorNameOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  public updatedSensorIdOptionsVisibility() {
    if (!this.sensorIdOptions.length) {
      return;
    }
    this.sensorIdOptions.forEach(idOption => idOption.disabled = false);
    this.model.temperatureSensorsHelperList.forEach(tempSensor => {

      if (tempSensor.sensorId) {
        this.sensorIdOptions.find(option => option.value === tempSensor.sensorId).disabled = true;
      }
      if ((tempSensor.pairMode === SensorModeType.PAIR || tempSensor.pairMode === SensorModeType.TRIO) && tempSensor.sensor2Id) {
        this.sensorIdOptions.find(option => option.value === tempSensor.sensor2Id).disabled = true;
      }
      if (tempSensor.pairMode === SensorModeType.TRIO && tempSensor.sensor3Id) {
        this.sensorIdOptions.find(option => option.value === tempSensor.sensor3Id).disabled = true;
      }
    })
  }

  getSensorIdOptionsForSensor(sensor: TemperatureSensorHelperModel): OptionModel[] {
    if (sensor.pairMode === SensorModeType.PAIR || sensor.pairMode === SensorModeType.TRIO) {
      let module: string;
      if (!!sensor.sensorId) {
        module = sensor.sensorId.split('.')[0];
      } else if (!!sensor.sensor2Id) {
        module = sensor.sensor2Id.split('.')[0];
      } else if (!!sensor.sensor3Id && sensor.pairMode === SensorModeType.TRIO) {
        module = sensor.sensor3Id.split('.')[0];
      }

      if (module) {
        return this.sensorIdOptions.filter(o => o.value.startsWith(module + '.'));
      }
    }
    return this.sensorIdOptions;
  }

  isSameTypeButNotEqualName(index: number, option: OptionModel): boolean {
    const helper = this.model.temperatureSensorsHelperList[index];
    if (helper.name) {
      return helper.type === option.sensorType && helper.index != option.index;
    }
    return false;
  }

  getCounter(index: number, modeType: string): number {
    let counter: number = 0;

    for (let i = 0; i < index; i++) {
      if (this.model.temperatureSensorsHelperList[i].pairMode === SensorModeType.TRIO) {
        counter += 3;
      } else if (this.model.temperatureSensorsHelperList[i].pairMode === SensorModeType.PAIR) {
        counter += 2;
      } else {
        counter++;
      }
    }

    let current = 1;
    switch (modeType) {
      case SensorModeType.SINGLE:
        current = 1;
        break;
      case SensorModeType.PAIR:
        current = 2;
        break;
      case SensorModeType.TRIO:
        current = 3;
        break;
    }

    return counter + current;
  }

  private parseTypeOutOfName(name: string): string {
    if (name) {
      const nameArray = name.split('_');
      nameArray.splice(nameArray.length - 1, 1);
      return nameArray.join('_');
    }
    return null;
  }

  private parseIndexOutOfName(name: string): number {
    if (name) {
      const nameArray = name.split('_');
      return Number.parseInt(nameArray[nameArray.length - 1], 10);
    }
    return null;
  }

  private prepareSingleSensor(nameEnum: TemperatureSensorNameEnum, shortNameEnum: TemperatureSensorNameShortEnum, optionEnum: TemperatureSensorOptionEnum) {
    for (let i = this.MIN_INDEX; i <= this.MAX_NUMBER_OF_SINGLES; i++) {
      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === nameEnum + i) === -1) {
        this.sensorNameOptions.push({
          label: nameEnum + i,
          labelShort: shortNameEnum + i,
          value: optionEnum + '_' + i,
          disabled: i !== this.MIN_INDEX,
          canBeActivated: true,
          index: i,
          sensorType: optionEnum
        });
      }
    }
  }

  private preparePairSensor(nameEnum: TemperatureSensorNameEnum, shortNameEnum: TemperatureSensorNameShortEnum, optionEnum: TemperatureSensorOptionEnum) {
    for (let i = this.MIN_INDEX; i <= this.MAX_NUMBER_OF_PAIRS; i++) {
      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === nameEnum + i) === -1) {
        this.sensorNameOptions.push({
          label: nameEnum + i,
          labelShort: shortNameEnum + i,
          value: optionEnum + '_' + i,
          disabled: i !== this.MIN_INDEX,
          canBeActivated: true,
          index: i,
          sensorType: optionEnum
        });
      }

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === nameEnum + i + TemperatureSensorNameEnum.FLOW_TEMP) === -1) {
        this.sensorNameOptions.push({
          label: nameEnum + i + TemperatureSensorNameEnum.FLOW_TEMP,
          labelShort: shortNameEnum + i + TemperatureSensorNameShortEnum.FLOW_TEMP,
          value: optionEnum + '_' + i + TemperatureSensorOptionEnum.FLOW_TEMP,
          disabled: true,
          canBeActivated: false,
          index: i,
          sensorType: optionEnum
        });
      }

      if (this.sensorNameOptions?.length <= 0 || this.sensorNameOptions.findIndex(item => item.label === nameEnum + i + TemperatureSensorNameEnum.RETURN_TEMP) === -1) {
        this.sensorNameOptions.push({
          label: nameEnum + i + TemperatureSensorNameEnum.RETURN_TEMP,
          labelShort: shortNameEnum + i + TemperatureSensorNameShortEnum.RETURN_TEMP,
          value: optionEnum + '_' + i + TemperatureSensorOptionEnum.RETURN_TEMP,
          disabled: true,
          canBeActivated: false,
          index: i,
          sensorType: optionEnum
        });
      }
    }

  }

  private prepareSensorIdOptions(deviceData: IDeviceData) {
    this.sensorIdOptions = [];
    for (const module of deviceData.uvsModule) {
      if (module.w1?.length > 0) {
        module.w1.sort((a, b) => +a.name - +b.name);
        for (const sensor of module.w1) {
          this.sensorIdOptions.push({value: module.moduleInfo.address + '.' + sensor.name, disabled: false});
        }
      }
    }

    this.MAX_NUMBER_OF_SINGLES = this.sensorIdOptions.length;
    this.MAX_NUMBER_OF_PAIRS = Math.ceil(this.sensorIdOptions.length / 2);
    this.MAX_NUMBER_OF_TRIOS = Math.ceil(this.sensorIdOptions.length / 3);

    this.model.temperatureSensorsHelperList.forEach(tempSensor => {

      if (tempSensor.sensorId) {
        const option = this.sensorIdOptions.find(option => option.value === tempSensor.sensorId);
        if (option) {
          option.disabled = true;
        } else {
          tempSensor.sensorId = null;
        }
      }
      if ((tempSensor.pairMode === SensorModeType.PAIR || tempSensor.pairMode === SensorModeType.TRIO) && tempSensor.sensor2Id) {
        const option = this.sensorIdOptions.find(option => option.value === tempSensor.sensor2Id);
        if (option) {
          option.disabled = true;
        } else {
          tempSensor.sensor2Id = null;
        }
      }
      if (tempSensor.pairMode === SensorModeType.TRIO && tempSensor.sensor3Id) {
        const option = this.sensorIdOptions.find(option => option.value === tempSensor.sensor3Id);
        if (option) {
          option.disabled = true;
        } else {
          tempSensor.sensor3Id = null;
        }
      }
    })
  }

  private publishModel(): void {
    this.shareService.sensorsTemperature = this.model;
  }

}
